import { useMemo, useState } from "react";
import { BookOpen, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { LOCAL_DOCS, searchDocs, type DocEntry } from "@/lib/local-docs";

interface Props {
  open: boolean;
  onClose: () => void;
}

const SUGGESTED_QUERIES = ["Map", "async", "array", "class"];

export function DocsPanel({ open, onClose }: Props) {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const results = useMemo(() => searchDocs(query), [query]);
  const selected = useMemo(
    () =>
      selectedId
        ? LOCAL_DOCS.find((d) => d.id === selectedId) ?? null
        : null,
    [selectedId]
  );

  function selectAndShow(d: DocEntry) {
    setSelectedId(d.id);
  }

  function quickSearch(q: string) {
    setQuery(q);
    const found = searchDocs(q);
    if (found.length === 1) setSelectedId(found[0].id);
  }

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 bg-black/40 transition-opacity z-40",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />
      <aside
        className={cn(
          "fixed top-0 right-0 bottom-0 w-full max-w-xl bg-card border-l border-border z-50 transition-transform shadow-2xl flex flex-col",
          open ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-[hsl(178_60%_45%)]" />
            <h2 className="text-sm font-semibold tracking-tight">Documentación</h2>
          </div>
          <Button size="iconSm" variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              autoFocus={open}
              placeholder="Buscar (ej: Map, Date, performance…)"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedId(null);
              }}
              className="pl-9 h-10 border-[hsl(178_60%_45%)]/30 focus-visible:ring-[hsl(178_60%_45%)]"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {/* Detalle (cuando hay seleccionado) */}
          {selected && (
            <DocDetail
              doc={selected}
              onBack={() => setSelectedId(null)}
            />
          )}

          {/* Empty state inicial */}
          {!selected && !query.trim() && (
            <EmptyHint onQuery={quickSearch} />
          )}

          {/* Resultados de búsqueda */}
          {!selected && query.trim() && (
            <ResultsList
              results={results}
              query={query}
              onSelect={selectAndShow}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border text-[11px] text-muted-foreground text-center">
          Documentación local · sin acceso a internet
        </div>
      </aside>
    </>
  );
}

// =================== EMPTY HINT ===================
function EmptyHint({ onQuery }: { onQuery: (q: string) => void }) {
  return (
    <div className="px-6 py-12 text-center">
      <div className="inline-flex h-10 w-10 items-center justify-center mb-4 text-muted-foreground/60">
        <Search className="h-6 w-6" />
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Escribí algo en el buscador para ver resultados.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <span className="text-[11px] text-muted-foreground">Ej:</span>
        {SUGGESTED_QUERIES.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onQuery(q)}
            className="font-mono text-[11px] bg-secondary hover:bg-accent text-foreground px-2 py-0.5 rounded transition-colors"
          >
            {q}
          </button>
        ))}
        <span className="text-[11px] text-muted-foreground">…</span>
      </div>
    </div>
  );
}

// =================== RESULTS LIST ===================
function ResultsList({
  results,
  query,
  onSelect,
}: {
  results: DocEntry[];
  query: string;
  onSelect: (d: DocEntry) => void;
}) {
  if (results.length === 0) {
    return (
      <div className="px-6 py-12 text-center text-sm text-muted-foreground">
        Sin resultados para <code className="font-mono text-foreground bg-secondary px-1.5 py-0.5 rounded">{query}</code>.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border">
      {results.map((d) => (
        <li key={d.id}>
          <button
            type="button"
            onClick={() => onSelect(d)}
            className="w-full text-left px-5 py-3 hover:bg-accent transition-colors"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold">{d.title}</span>
              <span className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
                {d.category}
              </span>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">{d.summary}</p>
          </button>
        </li>
      ))}
    </ul>
  );
}

// =================== DETAIL ===================
function DocDetail({ doc, onBack }: { doc: DocEntry; onBack: () => void }) {
  return (
    <div className="p-5">
      <button
        type="button"
        onClick={onBack}
        className="text-[11px] text-muted-foreground hover:text-foreground mb-3 transition-colors"
      >
        ← Volver a resultados
      </button>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-base font-semibold tracking-tight">{doc.title}</h3>
        <span className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
          {doc.category}
        </span>
      </div>
      <p className="text-sm text-foreground/80 leading-relaxed mb-4">
        {doc.summary}
      </p>
      <div className="rounded-md bg-background border border-border overflow-hidden">
        <div className="px-3 py-1.5 border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          Ejemplo
        </div>
        <pre className="p-3 text-[11px] leading-relaxed font-mono overflow-auto text-foreground/90">
          <code>{doc.example}</code>
        </pre>
      </div>
    </div>
  );
}
